import React, { useEffect, useState, memo, useMemo } from 'react';
import { useMEMTranslation } from '../../utils/text-utils';
import i18next from '../../i18n';
import { useTrackInteraction } from '../../services/logging/hooks';
import { usePageContext } from '../../utils/page-context';
import { Option, Combobox } from '../../providers/harmony-scope/lazy-enablers';
import { getDefaultPaymentPartition } from '../../ducks/filters/utils-filters';
import { TPaymentPartition } from '../../ducks/api-ducks/earnings-duck/types-earnings';
import { useAppSelector } from '../../ducks/hooks';
import { useMonthAndDay } from '../../utils/date-utils';

interface IProps {
  selectedPartitionGroup: string;
  selectedPartition: string;
  onChange: (partitionCadence: string) => void;
}

const formatPartition = (partition: TPaymentPartition) => partition && `${partition.codePeriod}`;

// This function relabels the codePeriods.
// The current order is based on the approval date.
// This covers transfers and true ups payments.
const reLabelPartitions = (monthAndDay: (date: Date | string) => string, partitions: TPaymentPartition[]) => {
  // first we sort the partitions by earnings end date.
  const newPartitions = [...partitions].sort((a, b) => {
    if (a.earningsEndDate > b.earningsEndDate) {
      return 1;
    }
    if (a.earningsEndDate < b.earningsEndDate) {
      return -1;
    }
    return 0;
  });
  // then we relabel the partitions adding dates
  return newPartitions.map((partition) => {
    let partitionLabel = `${partition.codePeriod} (${monthAndDay(partition.earningsStartDate)} - ${monthAndDay(partition.earningsEndDate)})`;
    // for true up payments we label them accordingly
    if (partition.isTrueUp) {
      partitionLabel = `${partitionLabel} ${i18next.t('filtersPanelTimePeriodLabelTrueUp')}`;
    }
    return { ...partition, codePeriod: partitionLabel };
  });
};

const componentName = 'Payment Partition Picker Filters Panel';
export const ComponentFilterPaymentsPartition = memo((props: IProps) => {
  // Props Accessors
  const { selectedPartitionGroup: selectedPartitionGroupNameProp, selectedPartition: selectedPartitionProp, onChange } = props;

  // Global state accessors
  const { availablePaymentPartitions: partitionGroups } = useAppSelector((state) => state.filters);

  // Hooks
  const monthAndDay = useMonthAndDay();
  const { t } = useMEMTranslation();
  const pageName = usePageContext();
  const trackInteraction = useTrackInteraction();
  const selectedPartitionGroup = useMemo(
    () => partitionGroups?.find((p) => p.partitionGroup === selectedPartitionGroupNameProp),
    [partitionGroups, selectedPartitionGroupNameProp]
  );
  const availablePartitions = useMemo(() => {
    if (!selectedPartitionGroup) return [];
    return [...reLabelPartitions(monthAndDay, selectedPartitionGroup.partitions)].sort((a, b) => {
      if (a.codePeriod > b.codePeriod) {
        return 1;
      }
      if (a.codePeriod < b.codePeriod) {
        return -1;
      }
      return 0;
    });
  }, [monthAndDay, selectedPartitionGroup]);

  // Local state
  const [selectedPartition, setSelectedPartition] = useState(selectedPartitionProp);

  // Effects
  useEffect(() => {
    const defaultPartition = getDefaultPaymentPartition(availablePartitions);
    if (!defaultPartition) return;
    setSelectedPartition(defaultPartition.id);
  }, [availablePartitions]);

  useEffect(() => {
    setSelectedPartition(selectedPartitionProp);
  }, [selectedPartitionProp]);

  useEffect(() => {
    onChange(selectedPartition);
  }, [onChange, selectedPartition]);

  // Handlers
  const partitionChanged = (event: any) => {
    if (!event.target.value) return;
    const newPartition = event.target.value;
    trackInteraction('Selected payment partition', pageName, componentName, {
      selectedPaymentPartition: newPartition,
    });
    setSelectedPartition(newPartition);
  };

  return availablePartitions.length ? (
    <>
      <Combobox label={t('filterPanelPaymentsLabel')} value={selectedPartition} onHeChange={partitionChanged}>
        {availablePartitions.map((p) => (
          <Option key={p.id} value={p.id} title={`${formatPartition(p)}`}>
            {`${formatPartition(p)}`}
          </Option>
        ))}
      </Combobox>
      <br />
    </>
  ) : null;
});
